/**
 * `@hyranai/regta-blocks` — Regta's block catalog.
 *
 * Content, not framework: every block here is a versioned compiler over
 * the vocabulary exported by `@hyranai/regta-document-ir`. A consumer that
 * wants Regta's legally-reviewed blocks imports this; a consumer that
 * wants to author its own imports only the framework and builds its own
 * registry, which our assembler and renderer accept unchanged.
 */
import { BlockRegistry } from '@hyranai/regta-document-ir';
import { describeDocumentType } from './common/capabilities.js';
import { getComposition, listCompositions } from './compositions/registry.js';
import { FRAMSTALLAN_BLOCKS } from './framstallan/blocks.js';
import { COMMON_BLOCKS } from './common/blocks.js';
import { SHARED_BLOCKS } from './common/shared.js';
import { FO_STATIC_CLAUSES } from './forhandlingsordning/clauses.js';
import { FO_BLOCKS } from './forhandlingsordning/blocks.js';
import { FOK_BLOCKS } from './forhandlingsoverenskommelse/blocks.js';
import { STRANDNING_BLOCKS } from './strandning/blocks.js';
import { LOKALHYRA_BLOCKS } from './lokalhyra/blocks.js';
import { BOSTADSHYRA_BLOCKS } from './bostadshyra/blocks.js';
import { STUDENTBOSTAD_BLOCKS } from './studentbostad/blocks.js';
import { CLEAN_TILLAGG_BOSTAD_BLOCKS } from './clean_tillagg_bostad/blocks.js';
import { BLOCKHYRA_BLOCKS } from './blockhyra/blocks.js';
import { LAGENHETSFORTECKNING_BLOCKS } from './lagenhetsforteckning/blocks.js';
import { CLEAN_BILAGA_BLOCKS } from './clean_bilaga/blocks.js';
import { GARAGE_BLOCKS } from './garage/blocks.js';
import { BILPLATS_BLOCKS } from './bilplats/blocks.js';
import { TILLAGGSAVTAL_HYRESGASTANPASSNING_BLOCKS } from './tillaggsavtal_hyresgastanpassning/blocks.js';
import { TILLAGGSAVTAL_TIDSBESTAMD_HYRA_BLOCKS } from './tillaggsavtal_tidsbestamd_hyra/blocks.js';
import { INDEXAVTAL_BLOCKS } from './indexavtal/blocks.js';
import { CLEAN_TILLAGG_LOKAL_BLOCKS } from './clean_tillagg_lokal/blocks.js';
import { ANDRING_HYRESVILLKOR_BLOCKS } from './andring_hyresvillkor/blocks.js';
import { ANDRING_HYRESVILLKOR_FORHANDLINGSKLAUSUL_BLOCKS } from './andring_hyresvillkor_forhandlingsklausul/blocks.js';
import { VILLKORSANDRING_54A_BLOCKS } from './villkorsandring_54a/blocks.js';
import { UPPSAGNING_VILLKORSANDRING_LOKAL_BLOCKS } from './uppsagning_villkorsandring_lokal/blocks.js';
import { UPPSAGNING_AVFLYTTNING_LOKAL_BLOCKS } from './uppsagning_avflyttning_lokal/blocks.js';
import { MEDDELANDE_18E_BLOCKS } from './meddelande_18e/blocks.js';
import { GODKANNANDE_18D_BLOCKS } from './godkannande_18d/blocks.js';
import { AVSTAENDE_BESITTNINGSSKYDD_BOSTAD_BLOCKS } from './avstaende_besittningsskydd_bostad/blocks.js';
import { AVSTAENDE_BESITTNINGSSKYDD_LOKAL_BLOCKS } from './avstaende_besittningsskydd_lokal/blocks.js';
import { ANSOKAN_ANDRAHAND_BLOCKS } from './ansokan_andrahand/blocks.js';
import { EVAKUERING_BLOCKS } from './evakuering/blocks.js';
import { PERSONUPPGIFTSAVTAL_BLOCKS } from './personuppgiftsavtal/blocks.js';
import { PERSONUPPGIFTSBILAGA_BLOCKS } from './personuppgiftsbilaga/blocks.js';
import { SAMTYCKE_SARSKILDA_AVTAL_BLOCKS } from './samtycke_sarskilda_avtal/blocks.js';
import { SAMTYCKE_SARSKILDA_BILAGA_BLOCKS } from './samtycke_sarskilda_bilaga/blocks.js';
import { stampBlockMinors, withFrozenBlocks } from './versioning/index.js';
export * from './lib.js';
export * from './clause.js';
export * from './common/index.js';
export * from './framstallan/options.js';
export { INCREASE_TYPE_OPTIONS, NOTICE_OPTIONS } from './forhandlingsoverenskommelse/blocks.js';
/**
 * Block definitions are namespaced by family: several documents legitimately
 * have a `mottagare` or an `overenskommelse`, and flattening them would force
 * the kind of prefixed names the `blockKey` already carries.
 */
export * as framstallan from './framstallan/blocks.js';
export * as forhandlingsordning from './forhandlingsordning/blocks.js';
export * as forhandlingsordningClauses from './forhandlingsordning/clauses.js';
export * as forhandlingsoverenskommelse from './forhandlingsoverenskommelse/blocks.js';
export * as strandning from './strandning/blocks.js';
export * as shared from './common/shared.js';
export * as common from './common/blocks.js';
export * as lokalhyra from './lokalhyra/blocks.js';
export * as bostadshyra from './bostadshyra/blocks.js';
export * as studentbostad from './studentbostad/blocks.js';
export * as cleanTillaggBostad from './clean_tillagg_bostad/blocks.js';
export * as blockhyra from './blockhyra/blocks.js';
export * as lagenhetsforteckning from './lagenhetsforteckning/blocks.js';
export * as cleanBilaga from './clean_bilaga/blocks.js';
export * as garage from './garage/blocks.js';
export * as bilplats from './bilplats/blocks.js';
export * as tillaggsavtalHyresgastanpassning from './tillaggsavtal_hyresgastanpassning/blocks.js';
export * as tillaggsavtalTidsbestamdHyra from './tillaggsavtal_tidsbestamd_hyra/blocks.js';
export * as indexavtal from './indexavtal/blocks.js';
export * as cleanTillaggLokal from './clean_tillagg_lokal/blocks.js';
export * as andringHyresvillkor from './andring_hyresvillkor/blocks.js';
export * as andringHyresvillkorForhandlingsklausul from './andring_hyresvillkor_forhandlingsklausul/blocks.js';
export * as villkorsandring54a from './villkorsandring_54a/blocks.js';
export * as uppsagningVillkorsandringLokal from './uppsagning_villkorsandring_lokal/blocks.js';
export * as uppsagningAvflyttningLokal from './uppsagning_avflyttning_lokal/blocks.js';
export * as meddelande18e from './meddelande_18e/blocks.js';
export * as godkannande18d from './godkannande_18d/blocks.js';
export * as avstaendeBesittningsskyddBostad from './avstaende_besittningsskydd_bostad/blocks.js';
export * as avstaendeBesittningsskyddLokal from './avstaende_besittningsskydd_lokal/blocks.js';
export * as ansokanAndrahand from './ansokan_andrahand/blocks.js';
export * as evakuering from './evakuering/blocks.js';
export * as personuppgiftsavtal from './personuppgiftsavtal/blocks.js';
export * as personuppgiftsbilaga from './personuppgiftsbilaga/blocks.js';
export * as samtyckeSarskildaAvtal from './samtycke_sarskilda_avtal/blocks.js';
export * as samtyckeSarskildaBilaga from './samtycke_sarskilda_bilaga/blocks.js';
export * from './compositions/forhandlingsframstallning.js';
export * from './compositions/forhandlingsordning.js';
export * from './compositions/forhandlingsoverenskommelse.js';
export * from './compositions/strandningsmeddelande.js';
export const REGTA_BLOCKS = withFrozenBlocks([
    ...FRAMSTALLAN_BLOCKS,
    ...FO_STATIC_CLAUSES,
    ...FO_BLOCKS,
    ...FOK_BLOCKS,
    ...STRANDNING_BLOCKS,
    ...LOKALHYRA_BLOCKS,
    ...BOSTADSHYRA_BLOCKS,
    ...STUDENTBOSTAD_BLOCKS,
    ...CLEAN_TILLAGG_BOSTAD_BLOCKS,
    ...BLOCKHYRA_BLOCKS,
    ...LAGENHETSFORTECKNING_BLOCKS,
    ...CLEAN_BILAGA_BLOCKS,
    ...GARAGE_BLOCKS,
    ...BILPLATS_BLOCKS,
    ...TILLAGGSAVTAL_HYRESGASTANPASSNING_BLOCKS,
    ...TILLAGGSAVTAL_TIDSBESTAMD_HYRA_BLOCKS,
    ...INDEXAVTAL_BLOCKS,
    ...CLEAN_TILLAGG_LOKAL_BLOCKS,
    ...ANDRING_HYRESVILLKOR_BLOCKS,
    ...ANDRING_HYRESVILLKOR_FORHANDLINGSKLAUSUL_BLOCKS,
    ...VILLKORSANDRING_54A_BLOCKS,
    ...UPPSAGNING_VILLKORSANDRING_LOKAL_BLOCKS,
    ...UPPSAGNING_AVFLYTTNING_LOKAL_BLOCKS,
    ...MEDDELANDE_18E_BLOCKS,
    ...GODKANNANDE_18D_BLOCKS,
    ...AVSTAENDE_BESITTNINGSSKYDD_BOSTAD_BLOCKS,
    ...AVSTAENDE_BESITTNINGSSKYDD_LOKAL_BLOCKS,
    ...ANSOKAN_ANDRAHAND_BLOCKS,
    ...EVAKUERING_BLOCKS,
    ...PERSONUPPGIFTSAVTAL_BLOCKS,
    ...PERSONUPPGIFTSBILAGA_BLOCKS,
    ...SAMTYCKE_SARSKILDA_AVTAL_BLOCKS,
    ...SAMTYCKE_SARSKILDA_BILAGA_BLOCKS,
    ...SHARED_BLOCKS,
    ...COMMON_BLOCKS,
]);
stampBlockMinors(REGTA_BLOCKS);
export { BLOCK_LOCK_OVERRIDES, BLOCK_MINORS, FROZEN_BLOCKS, REQUIRED_USAGE_PRODUCTS, blockVersionSamples, withFrozenBlocks } from './versioning/index.js';
/** The catalog Regta serves. A consumer may `merge()` its own into a copy. */
export const createRegtaBlockRegistry = () => new BlockRegistry(REGTA_BLOCKS);
export * from './compositions/registry.js';
/**
 * Every document type in the composition registry, described for a consumer
 * (title, meta, slots, edit capabilities — see `describeDocumentType`).
 */
export function listDocumentTypes(registry = createRegtaBlockRegistry()) {
    return listCompositions().flatMap(({ documentKey, documentVersion }) => {
        const composition = getComposition(documentKey, documentVersion);
        return composition ? [describeDocumentType(composition, registry)] : [];
    });
}
//# sourceMappingURL=index.js.map